
import { Theme, SxProps as StylesSxProps } from '@mui/material/styles';
import MuiIcon from '@mui/material/Icon';
import SvgIcon from '@mui/material/SvgIcon';
import iconDef, { IconType } from './Utils/IconDefinitions';
import { CommonPropsAndTypes } from '../_Types/StandardComponentProps';
import { AvenyThemeSpacing } from '../ThemeProvider/AvenyTheme';
import { styled } from '@mui/material';
import { spacing, SpacingProps, SxProps, SystemStyleObject } from '@mui/system';

// Used to generate testable size names
export const iconSizeTypeDef = {
  default: null,
  large: null,
  extraLarge: null,
  small: null,
  medium: null,
  special: null,
};

export type IconColor =
  | CommonPropsAndTypes.ColorType
  | 'action'
  | 'disabled'
  | 'warning'
  | 'error'
  | 'lowEmphasis'
  | 'white';

export type IconSize = keyof typeof iconSizeTypeDef;

export type IconVerticalAlign =
  | 'middle'
  | 'top'
  | 'bottom'
  | 'text-top'
  | 'text-bottom'
  | 'sub';

interface IProps extends SpacingProps {
  iconType: IconType;
  className?: string;
  color?: IconColor;
  //** Overrides the prop color for own defined svgs  */
  customSvgColor?: string;
  size?: IconSize;
  overlay?: React.ReactNode;
  disableTabbing?: boolean;
  testId?: string;
  chipRadioButtonIcon?: boolean;
  selectedIcon?: boolean;
  avatarChildren?: boolean;
  chipAvatarDemo?: boolean;
  verticalAlign?: IconVerticalAlign;
}

const getIconSizeStyles = (
  theme: Theme,
  size: IconSize | undefined
): SystemStyleObject<Theme> => {
  const sizes = {
    size_default: {
      width: theme.spacing(3),
      height: theme.spacing(3),
      fontSize: theme.typography.pxToRem(3 * AvenyThemeSpacing),
    },
    size_special: {
      width: theme.spacing(4.5),
      height: theme.spacing(4.5),
      fontSize: theme.typography.pxToRem(6 * AvenyThemeSpacing),
    },
    size_large: {
      width: theme.spacing(6),
      height: theme.spacing(6),
      fontSize: theme.typography.pxToRem(6 * AvenyThemeSpacing),
    },
    size_extraLarge: {
      width: theme.spacing(9),
      height: theme.spacing(9),
      fontSize: theme.typography.pxToRem(9 * AvenyThemeSpacing),
    },
    size_medium: {
      width: theme.spacing(2.5),
      height: theme.spacing(2.5),
      fontSize: theme.typography.pxToRem(2.5 * AvenyThemeSpacing),
    },
    size_small: {
      width: theme.spacing(2),
      height: theme.spacing(2),
      fontSize: theme.typography.pxToRem(2 * AvenyThemeSpacing),
    },
  };

  return sizes[`size_${size || 'default'}`];
};

// Used for testing
export const iconSizeTypes = Object.keys(iconSizeTypeDef);

/**
 * Använder MaterialUI Icon eller SvgIcon beroende på iconType
 */
const EDIconBase: React.FC<React.PropsWithChildren<IProps>> = props => {
  const warning = props.color === 'warning';
  const iconColor =
    props.color &&
    props.color !== 'warning' &&
    props.color !== 'lowEmphasis' &&
    props.color !== 'white'
      ? props.color
      : 'secondary';

  const sx: SxProps<Theme> = [
    {
      display: 'inline-block',
      verticalAlign: props.verticalAlign || 'middle',
      outline: 'none',
    },
    theme => ({ '&&': getIconSizeStyles(theme, props.size) } as const),
    warning && { color: theme => theme.customProperties.Warning.main },
    props.color === 'lowEmphasis' && {
      color: theme => theme.customProperties.Fade.DarkOnLight.lowEmphasis,
    },
    props.color === 'white' && {
      color: theme => theme.palette.common.white,
    },
    Boolean(props.chipRadioButtonIcon) && { position: 'absolute', left: 0 },
    Boolean(props.avatarChildren) && { height: '24px', width: '24px' },
    Boolean(props.chipAvatarDemo) && {
      marginLeft: theme => theme.spacing(1),
      width: 24,
      height: 24,
    },
    Boolean(props.selectedIcon) && {
      '&&': {
        color: 'inherit',
      },
    },
  ] as const;

  const svgElement = iconDef(props.iconType);
  return svgElement ? (
    <SvgIcon
      tabIndex={props.disableTabbing ? -1 : undefined}
      className={props.className}
      color={props.customSvgColor ? undefined : iconColor}
      htmlColor={props.customSvgColor}
      data-testid={props.testId ?? props.iconType}
      sx={sx as StylesSxProps<Theme>}
    >
      {svgElement}
    </SvgIcon>
  ) : (
    <MuiIcon
      tabIndex={props.disableTabbing ? -1 : undefined}
      className={props.className}
      color={iconColor}
      data-testid={props.testId ?? props.iconType}
      sx={sx as StylesSxProps<Theme>}
    >
      {props.iconType}
    </MuiIcon>
  );
};

const EDIcon: React.FC<React.PropsWithChildren<IProps>> = styled(EDIconBase, {
  name: 'EDIcon',
})(spacing);
export default EDIcon;
